import {
  type AlbumImage,
  type ExtendedAlbumUUID,
  type GetUploadUrlResponse,
  type IImageServiceClient,
  type ImagesValidationRequest,
  type ImagesValidationResponse,
  type MoveToTrashRequest,
  ValidationStatus
} from '@pixum/types';
import type { Response } from 'ts-retrofit';
import { HTTP_METHODS, HTTP_STATUS_CODE, IMAGE_SERVICE, getBaseUrl } from 'utils';
import { ClientFetch } from '../../fetch/ClientFetch';

const base = () => `${getBaseUrl()}${IMAGE_SERVICE.PATH}`;

const toResponse = <T>(data: T, status: number): Response<T> => ({ data, status }) as unknown as Response<T>;

export class ImageServiceClientAdapter implements IImageServiceClient {
  private readonly fetch = new ClientFetch();
  private readonly serverValidationStatusByUuid = new Map<string, ValidationStatus>();

  async getUploadUrl() {
    const data = await this.fetch.makeRequest<GetUploadUrlResponse>(`${base()}${IMAGE_SERVICE.ENDPOINTS.UPLOAD_URL}`);

    return toResponse(data, HTTP_STATUS_CODE.OK);
  }

  async retrieveLatestImages(limit = 10, withoutTrash = true) {
    const params = new URLSearchParams({
      limit: String(limit),
      withoutTrash: String(withoutTrash)
    });
    const data = await this.fetch.makeRequest<{ images: AlbumImage[] }>(
      `${base()}${IMAGE_SERVICE.ENDPOINTS.LATEST_IMAGES}?${params}`
    );

    return toResponse(data, HTTP_STATUS_CODE.OK);
  }

  async validateImage(
    body: ImagesValidationRequest,
    albumId: ExtendedAlbumUUID
  ): Promise<Response<ImagesValidationResponse>> {
    try {
      const data = await this.fetch.makeRequest<ImagesValidationResponse>(
        `${base()}${IMAGE_SERVICE.ENDPOINTS.VALIDATE}?uploadSource=SITE&addToAlbum=${albumId}`,
        { method: HTTP_METHODS.POST, body }
      );

      let hasInvalid = false;

      data.images?.forEach(image => {
        if (image.status !== ValidationStatus.VALID) {
          hasInvalid = true;
          this.serverValidationStatusByUuid.set(image.uuid, image.status);
        }
      });

      return toResponse(data, hasInvalid ? HTTP_STATUS_CODE.UNPROCESSABLE_CONTENT : HTTP_STATUS_CODE.CREATED);
    } catch (err) {
      const status =
        typeof err === 'object' && err !== null && 'statusCode' in err
          ? Number((err as { statusCode: unknown }).statusCode)
          : HTTP_STATUS_CODE.BAD_REQUEST;

      return toResponse({} as ImagesValidationResponse, status);
    }
  }

  getValidationStatus(uuid: string): ValidationStatus | undefined {
    return this.serverValidationStatusByUuid.get(uuid);
  }

  async moveToTrash(body: MoveToTrashRequest) {
    await this.fetch.makeRequest<void>(`${base()}${IMAGE_SERVICE.ENDPOINTS.MOVE_TO_TRASH}`, {
      method: HTTP_METHODS.PUT,
      body
    });

    return toResponse(undefined as unknown as void, HTTP_STATUS_CODE.OK);
  }

  async saveFaces(imageId: string, body: { faces: unknown[] }) {
    await this.fetch.makeRequest<void>(`${base()}${IMAGE_SERVICE.ENDPOINTS.FACES.replace(':imageId', imageId)}`, {
      method: HTTP_METHODS.PUT,
      body
    });

    return toResponse(undefined as unknown as void, HTTP_STATUS_CODE.OK);
  }
}
